import React, { useMemo } from "react";
import { useTheme } from "@/context/ThemeContext";
import { HUNGARY_COUNTIES, COUNTY_PATHS, COUNTY_LABELS } from "@/lib/hungaryCountyPaths";

/**
 * Interaktív Magyarország megyetérkép — sötét/világos téma, több megye kiválasztható.
 * 0 autós megyék szürke zónák (nem kattinthatók).
 */
export function HungaryCountyMap({ selected = [], onToggle, counts = {}, className = "" }) {
  const { theme } = useTheme();
  const isLight = theme === "light";

  const palette = useMemo(
    () =>
      isLight
        ? {
            bg: "#f1f5f9",
            fill: "#cbd5e1",
            stroke: "#94a3b8",
            hover: "#93c5fd",
            selected: "#2563eb",
            selectedStroke: "#1d4ed8",
            disabled: "#e2e8f0",
            disabledStroke: "#cbd5e1",
            label: "#334155",
            labelDisabled: "#94a3b8",
            count: "#64748b",
          }
        : {
            bg: "#0f172a",
            fill: "#1e293b",
            stroke: "#334155",
            hover: "#1e3a5f",
            selected: "#3b82f6",
            selectedStroke: "#60a5fa",
            disabled: "#111827",
            disabledStroke: "#1f2937",
            label: "#e2e8f0",
            labelDisabled: "#475569",
            count: "#94a3b8",
          },
    [isLight],
  );

  const toggle = (county) => {
    const n = counts[county] || 0;
    if (n === 0) return;
    onToggle?.(county);
  };

  return (
    <div
      className={`relative rounded-2xl overflow-hidden ${className}`}
      style={{ background: palette.bg }}
      data-testid="hungary-county-map"
    >
      <svg viewBox="0 0 1000 520" className="w-full h-auto select-none" role="img" aria-label="Magyarország megyetérképe">
        {HUNGARY_COUNTIES.map((county) => {
          const n = counts[county] || 0;
          const disabled = n === 0;
          const active = selected.includes(county);
          const fill = disabled ? palette.disabled : active ? palette.selected : palette.fill;
          const stroke = disabled ? palette.disabledStroke : active ? palette.selectedStroke : palette.stroke;
          const [lx, ly] = COUNTY_LABELS[county] || [500, 260];

          return (
            <g key={county}>
              <path
                d={COUNTY_PATHS[county]}
                fill={fill}
                stroke={stroke}
                strokeWidth={active ? 2 : 1}
                className={disabled ? "cursor-not-allowed" : "cursor-pointer transition-colors duration-200"}
                style={!disabled && !active ? { transition: "fill 0.15s" } : undefined}
                onMouseEnter={(e) => {
                  if (disabled || active) return;
                  e.currentTarget.setAttribute("fill", palette.hover);
                }}
                onMouseLeave={(e) => {
                  if (disabled || active) return;
                  e.currentTarget.setAttribute("fill", palette.fill);
                }}
                onClick={() => toggle(county)}
                data-testid={`county-path-${county}`}
                aria-label={`${county}${n ? `, ${n} autó` : ", nincs autó"}`}
              />
              {!disabled && (
                <text
                  x={lx}
                  y={ly}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  pointerEvents="none"
                  fill={active ? "#fff" : palette.label}
                  fontSize={county === "Budapest" ? 9 : county.length > 14 ? 7 : 8}
                  fontWeight={active ? 600 : 500}
                  className="font-sans"
                >
                  {county === "Budapest" ? "Bp." : county.length > 16 ? "" : county.split("-")[0].split(" ")[0]}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-2 text-[10px]" style={{ color: palette.count }}>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm inline-block" style={{ background: palette.selected }} /> Kiválasztva
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm inline-block border" style={{ background: palette.fill, borderColor: palette.stroke }} /> Elérhető
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm inline-block" style={{ background: palette.disabled }} /> Nincs autó
        </span>
      </div>
    </div>
  );
}
